import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  APIProvider,
  Map as GoogleMap,
  MapControl,
  ControlPosition,
  useMap,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import type { GoogleMapsOverlay } from "@deck.gl/google-maps";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Plus, Minus, Layers, ChevronLeft, ChevronRight } from "lucide-react";

import {
  MapProps,
  MapMarkerData,
  MapItem,
  MapRenderMode,
  MapChoroplethConfig,
  MapChoroplethDatum,
} from "./types";
import {
  buildChoroplethScale,
  colorFor,
  formatChoroplethFraction,
  formatChoroplethValue,
  isInsufficient,
  CHOROPLETH_FILL_ALPHA,
  CHOROPLETH_INSUFFICIENT_ALPHA,
  CHOROPLETH_STROKE_ALPHA,
  CHOROPLETH_STROKE_COLOR,
} from "./choropleth";
import { MapChoroplethLegend } from "./MapChoroplethLegend";
import {
  MAP_STYLES,
  MAP_EXTRA_LIBRARIES,
  DEFAULT_MAP_CONFIG,
  createClusterSvg,
  dotPillSvg,
  dotRadiusForPill,
  STACK_SIZE_PREMIUM,
  HEATMAP_RADIUS,
  HEATMAP_OPACITY,
  HEATMAP_COLOR_RANGE,
  HEATMAP_DEBOUNCE_MS,
} from "./constants";
import { MapDrawLayer } from "./MapDrawLayer";

/**
 * Fallback pin colour: brand purple, the VERIFIED state.
 *
 * It used to be #EA4335 (Google's default red), which meant "we have no colour for this
 * pin" rendered identically to "this scan is alarming" — a pin whose fillColor failed to
 * resolve for ANY reason screamed alert over perfectly clean scans. A missing value must
 * fail to the calm default and let state move it, never the other way round.
 */
const DEFAULT_PIN_COLOR = "#7F48DF";

/** Web-Mercator world width at zoom 0. Google's tile size; one world = this * 2^zoom. */
const WORLD_TILE_PX = 256;
/**
 * Absolute floor for the computed zoom-out limit. A container narrower than 256px would
 * otherwise compute a negative floor, which Google rejects, and there is no useful map
 * below this anyway.
 */
const MIN_WORLD_ZOOM = 1;

/*
  VRG-699 — the `frameWorld` view: the whole world at its natural ratio.

  Google draws Web Mercator, where one world at the one-world zoom is SQUARE (256 * 2^z
  on both axes, ±85.05°). A full ±85° square is too tall for a card, and the far north and
  south are almost empty, so the frame is cut at 80°N / 60°S: everything inhabited, with
  Antarctica dropped. In Mercator units (y = ln(tan(π/4 + φ/2))) that band is
  y(80°) − y(−60°) = 2.436 + 1.317 = 3.753 high against a world 2π = 6.283 wide, so the
  container is 6.283 / 3.753 ≈ 1.674 : 1, and its centre is the latitude of the band's
  Mercator midpoint (y = 0.560 → ≈ 30.5°N), not the arithmetic midpoint (10°N), which
  would push the Arctic off the top.

  The fixed pixel height this replaces is why the live map showed only the north: at the
  one-world zoom a 691px-wide map is 691px of Mercator tall, and a 320px box showed under
  half of it.
*/
const WORLD_FRAME_NORTH = 80;
const WORLD_FRAME_SOUTH = -60;
const mercatorY = (latDeg: number): number =>
  Math.log(Math.tan(Math.PI / 4 + (latDeg * Math.PI) / 360));
const latFromMercatorY = (y: number): number =>
  (Math.atan(Math.exp(y)) * 360) / Math.PI - 90;
const WORLD_FRAME_Y_SPAN =
  mercatorY(WORLD_FRAME_NORTH) - mercatorY(WORLD_FRAME_SOUTH);
/** Width : height of a `frameWorld` map (≈ 1.674). Exported so a skeleton matches it. */
export const WORLD_FRAME_ASPECT_RATIO = (2 * Math.PI) / WORLD_FRAME_Y_SPAN;
/** Camera centre of a `frameWorld` map (≈ 30.5°N, 0°). */
export const WORLD_FRAME_CENTER = {
  lat: latFromMercatorY((mercatorY(WORLD_FRAME_NORTH) + mercatorY(WORLD_FRAME_SOUTH)) / 2),
  lng: 0,
};

// Custom marker: a filled status dot with a soft same-color GLOW (no white ring) so it
// reads as native-to-dark and separates from the map tiles by luminance, not a hard
// outline. Rendered hi-res (crisp when scaled down); centered with margin for the glow.
const createCustomMarkerSvg = (fillColor = DEFAULT_PIN_COLOR): string => {
  return `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <defs>
        <filter id="dot-glow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <!-- soft colored glow halo -->
      <circle cx="22" cy="22" r="9" fill="${fillColor}" opacity="0.55" filter="url(#dot-glow)" />
      <!-- solid dot on top -->
      <circle cx="22" cy="22" r="7" fill="${fillColor}" />
    </svg>`;
};

// Several scans at the SAME coordinates (repeat scans of one item at one address)
// painted N identical dots exactly on top of each other, so a stack was
// indistinguishable from a single scan and only the top marker was clickable.
//
// A stack now renders as a NORMAL status dot with a small "+N" pill beside it, rather
// than replacing the dot with a big counted disc. Two reasons: the disc was a loud
// white blob on a dark map and dominated the pins it was meant to annotate, and being
// much larger than its neighbours it misrepresented one spot as more important. The
// pill annotates; the dot still reads as a scan at that place.
//
// Everything is kept well inside the 44x44 box (pill ends at x=42, shadow region is
// tight) because the icon is rasterised at exactly the viewBox and anything straying
// outside gets clipped — which is what chopped the previous disc's top and bottom.
//
// Delegates to the shared `dotPillSvg` in ./constants — a cluster marker (see
// `createClusterSvg` there) renders aggregation the SAME way, and the geometry is
// factored into one place so the two cannot drift apart (working-conventions §36).
const createStackDotSvg = (
  count: number,
  fillColor = DEFAULT_PIN_COLOR,
  isDark = false,
): string => dotPillSvg(count, fillColor, dotRadiusForPill(STACK_SIZE_PREMIUM), isDark);

// Stacking is for scans at TRULY the same spot, not merely nearby ones: 6dp ≈ 11cm,
// which absorbs float noise without merging two genuinely distinct locations. Nearby-
// but-distinct scans stay separate pins and are grouped by the clusterer instead.
const COINCIDENT_DP = 6;
const positionKey = (lat: number, lng: number): string =>
  `${lat.toFixed(COINCIDENT_DP)},${lng.toFixed(COINCIDENT_DP)}`;

// Dots grow as you zoom in so a single scan reads bigger up close (and stays
// unobtrusive when zoomed out over the whole world). Clamped so it never gets
// tiny at world-view or huge at street-level. z2 → 20px, z8 → ~26px, z13 → ~34px,
// z20+ → capped 42px.
const dotSizeForZoom = (zoom: number): number =>
  Math.round(Math.max(20, Math.min(42, 12 + (zoom ?? 4) * 1.6)));

// Radar pulse for the SELECTED dot — a DOM overlay, because the native marker icons are
// raster SVGs and can't animate. Two staggered rings expand + fade in the dot's own color.
const PULSE_CSS_ID = "vera-map-pulse-style";
const ensurePulseCss = () => {
  if (typeof document === "undefined" || document.getElementById(PULSE_CSS_ID)) return;
  const style = document.createElement("style");
  style.id = PULSE_CSS_ID;
  style.textContent =
    "@keyframes veraRadar{0%{transform:translate(-50%,-50%) scale(.5);opacity:.45}60%{opacity:.08}100%{transform:translate(-50%,-50%) scale(2.8);opacity:0}}" +
    ".vera-pulse-ring{position:absolute;left:0;top:0;width:48px;height:48px;border-radius:50%;pointer-events:none}";
  document.head.appendChild(style);
};

// Lazily builds a radar-pulse OverlayView (google.maps is guaranteed loaded once we have a map).
const makePulseOverlay = (pos: google.maps.LatLng, color: string): google.maps.OverlayView => {
  ensurePulseCss();
  const overlay = new google.maps.OverlayView();
  let div: HTMLDivElement | null = null;
  overlay.onAdd = function () {
    div = document.createElement("div");
    div.style.position = "absolute";
    for (let i = 0; i < 2; i++) {
      const ring = document.createElement("div");
      ring.className = "vera-pulse-ring";
      // Outline ring (radar sweep), not a filled disc — a fill reads as a growing blob.
      ring.style.border = `2px solid ${color}`;
      // `backwards` so a delayed ring shows the 0% keyframe (small, faint) during its
      // delay rather than its full base size (the "weird dot" on first selection).
      ring.style.animation = `veraRadar 3s ease-out ${i * 1.5}s infinite backwards`;
      div.appendChild(ring);
    }
    (this as google.maps.OverlayView).getPanes()?.overlayLayer.appendChild(div);
  };
  overlay.draw = function () {
    if (!div) return;
    const p = (this as google.maps.OverlayView).getProjection()?.fromLatLngToDivPixel(pos);
    if (p) {
      div.style.left = `${p.x}px`;
      div.style.top = `${p.y}px`;
    }
  };
  overlay.onRemove = function () {
    div?.remove();
    div = null;
  };
  return overlay;
};

/**
 * `pulseItems` — a radar ring on every plotted pin (the website's live visitors). Its own
 * layer, so the selected-pin pulse in `MapContent` is untouched; pins mode only, since
 * heat and choropleth draw no pins to pulse. Rebuilt when the set of points changes.
 */
const MapPulseLayer: React.FC<{
  items: MapItem[];
  enabled: boolean;
  renderMode: MapProps["renderMode"];
}> = ({ items, enabled, renderMode }) => {
  const gmap = useMap();
  const signature = enabled
    ? items.map((m) => `${m.lat},${m.lng},${m.fillColor ?? ""}`).join("|")
    : "";
  useEffect(() => {
    if (!gmap || !signature || (renderMode && renderMode !== "pins")) return undefined;
    const overlays: google.maps.OverlayView[] = [];
    for (const m of items) {
      // Same guard as the selected pulse: `LatLng` throws on a non-finite argument.
      const lat = Number(m.lat);
      const lng = Number(m.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const overlay = makePulseOverlay(
        new google.maps.LatLng(lat, lng),
        m.fillColor || DEFAULT_PIN_COLOR,
      );
      overlay.setMap(gmap);
      overlays.push(overlay);
    }
    return () => overlays.forEach((o) => o.setMap(null));
    // `signature` stands in for `items`: a parent re-render with the same points must
    // not tear the rings down and restart their animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmap, signature, renderMode]);
  return null;
};

/**
 * The two render modes that paint through the SHARED deck.gl overlay.
 *
 * Their effects run on every `renderMode` change and both write to one `overlayRef`, so
 * each has to know when the other owns it — otherwise the surviving layer set depends on
 * the order the two `useEffect` calls happen to appear in this file. See the note in
 * heat's cleanup branch for the concrete failure that produced.
 */
const isDeckMode = (mode: MapRenderMode): boolean =>
  mode === "heat" || mode === "choropleth";

/** The shape this file needs from the country geometry asset. Deliberately minimal —
 *  deck.gl consumes the raw document, and narrowing it further here would just be a
 *  second, drifting description of a file another script generates. */
interface CountryGeoJson {
  type: string;
  features: {
    type: string;
    properties: {a3: string; a2: string | null; name: string};
    geometry: unknown;
  }[];
}

const escapeHtml = (s: string): string =>
  String(s).replace(
    /[&<>"]/g,
    (c) => ({"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;"}[c] as string),
  );

/**
 * A country's hover tooltip.
 *
 * ⚠️ THE NUMERATOR AND DENOMINATOR ARE NOT OPTIONAL. A percentage on its own is not a
 * reportable figure and, worse, it is exactly what makes a country at "50% (1 of 2)"
 * indistinguishable from one at "50% (5,000 of 10,000)" — the whole small-sample hazard
 * this mode is built around. So the fraction renders on every row, for counts as well as
 * rates (where it reads as the plain total and costs a line nobody has to decode).
 *
 * A suppressed row says so IN WORDS instead of showing a rate. Rendering the number with
 * a caveat beside it would still put "50%" in front of a reader whose eye takes the big
 * figure and leaves the caveat — the suppression has to remove the misleading number, not
 * annotate it.
 */
const buildChoroplethTip = (
  d: MapChoroplethDatum,
  config: MapChoroplethConfig,
): string => {
  const suppressed = isInsufficient(d, config.kind, config.minDenominator);
  const headline = suppressed
    ? "Too few scans to report a rate"
    : formatChoroplethValue(d.value, config.kind);
  return (
    `<div class="vera-map-tip">` +
    `<div class="vmt-name">${escapeHtml(d.name)}</div>` +
    `<div class="vmt-sub">${escapeHtml(config.label)}: ${escapeHtml(headline)}</div>` +
    `<div class="vmt-sub">${escapeHtml(formatChoroplethFraction(d))}</div>` +
    `</div>`
  );
};

// Tooltip HTML, shared by hover + the persistent selected-marker popover. A consumer
// supplies rich markup via item.tooltipHtml (owns escaping); else we escape the title.
const buildTip = (m: MapMarkerData): string => {
  const richHtml = (m.item as any).tooltipHtml as string | undefined;
  const tip = String((m.item as any).title || "");
  return richHtml
    ? `<div class="vera-map-tip">${richHtml}</div>`
    : tip
      ? `<div class="vera-map-tip">${tip.replace(/[&<>"]/g, (c) =>
          ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string),
        )}</div>`
      : "";
};

const MapContent: React.FC<{
  items: MapMarkerData[];
  onMarkerClick: (index: number) => void;
  /** See `MapProps.clusterRenderer`. */
  clusterRenderer?: (count: number, accentColor?: string, isDark?: boolean) => string;
  enableClustering?: boolean;
  activeItemId?: string;
  fitToItems?: boolean;
  /** See `MapProps.suppressFit`. Skips one fitToItems camera move without losing track
   *  of the item-set signature. Superseded by `fitSignature` — see that prop. */
  suppressFit?: boolean;
  /** See `MapProps.fitSignature`. */
  fitSignature?: string;
  /** See `MapProps.renderMode`. */
  renderMode?: MapRenderMode;
  /** See `MapProps.choropleth`. */
  choropleth?: MapChoroplethConfig;
  /** See `MapProps.onChoroplethUnavailable`. */
  onChoroplethUnavailable?: () => void;
  /** Heat could not be rendered (vendor removed the layer) — consumer should fall back. */
  onHeatUnavailable?: () => void;
  onDeselect?: () => void;
  renderStackTooltip?: (items: MapItem[]) => string;
  /**
   * Hand the stack's members to the consumer instead of opening a tooltip on the map.
   *
   * Preferred over `renderStackTooltip`: the tooltip is an InfoWindow, so its content has
   * to be an HTML STRING, which puts it outside React and therefore outside the design
   * system — no shared chip, no themed avatar, no app font, and a width ceiling set by
   * the map card. Routing the members out lets the consumer render them as real
   * components wherever it already shows scan detail. `renderStackTooltip` stays for
   * callers that have not migrated.
   */
  onStackClick?: (items: MapItem[]) => void;
  path?: { lat: number; lng: number }[];
  pathColor?: string;
  /** See `MapProps.relayoutNonce`. */
  relayoutNonce?: number | string;
  /**
   * See `MapProps.mapStyle`. Needed down here only so the aggregation markers (stack +
   * cluster "+N" pills) can invert on a dark basemap — a white pill reads as a bright
   * blob on dark tiles, and the count on it was the thing being complained about.
   */
  mapStyle?: MapProps["mapStyle"];
  /** See `MapProps.frameWorld`. */
  frameWorld?: boolean;
}> = ({
  items,
  onMarkerClick,
  clusterRenderer,
  enableClustering = true,
  activeItemId,
  fitToItems = false,
  suppressFit = false,
  fitSignature,
  renderMode = "pins",
  choropleth,
  onChoroplethUnavailable,
  onHeatUnavailable,
  onDeselect,
  renderStackTooltip,
  onStackClick,
  path,
  pathColor,
  relayoutNonce,
  mapStyle,
  frameWorld = false,
}) => {
  const gmap = useMap();
  /**
   * Positive dark signal only — see `dotPillSvg`'s header. `vitamin-c` and `custom` keep
   * the white pill because a white pill survives an unknown basemap and a zinc one does
   * not. This is intentionally NOT the `mapStyle === "light" ? … : …` test the chrome
   * further down uses; that one is picking a panel background, this one is picking a
   * marker that sits on the tiles.
   */
  const isDarkBasemap = mapStyle === "dark";
  const nativeMarkersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const hasZoomedToLastRef = useRef<boolean>(false);
  const prevActiveRef = useRef<string | undefined>(undefined);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  // Separate popover for the SELECTED marker so it stays open while other markers hover.
  const activeTipRef = useRef<google.maps.InfoWindow | null>(null);
  const pulseRef = useRef<google.maps.OverlayView | null>(null);
  const shownActiveRef = useRef<string | undefined>(undefined);
  // Popover listing every scan at a stacked point. Separate from the hover tip so it
  // survives the pointer leaving the marker — it's a thing you read, not a flyby.
  const stackTipRef = useRef<google.maps.InfoWindow | null>(null);
  // Which stack's list is currently open. Needed so the marker's hover tip can stay
  // out of the way: with the list open the tip is both redundant ("click to list them"
  // when they're already listed) and physically on top of what it's inviting you to read.
  const openStackKeyRef = useRef<string | undefined>(undefined);
  const closeStackTip = useCallback(() => {
    stackTipRef.current?.close();
    openStackKeyRef.current = undefined;
  }, []);
  // The choropleth's hover popover. Its OWN InfoWindow rather than the pins one: the two
  // modes are never on screen together, but they are torn down on different schedules
  // (the pins effect rebuilds its window whenever the marker set changes), and sharing
  // one would make a mode switch able to close a window the other mode had just opened.
  const choroplethTipRef = useRef<google.maps.InfoWindow | null>(null);
  /** Which country's tooltip content is currently built. See the `onHover` note. */
  const hoveredCountryRef = useRef<string | undefined>(undefined);

  /**
   * A string that changes exactly when a country's FILL would change.
   *
   * This exists only to feed deck.gl's `updateTriggers`, and it is load-bearing: the
   * layer's `data` is the same 240-feature geometry on every render, so deck.gl has no
   * way to notice that the accessor's captured `byCode` now holds different values. Left
   * out, the fills freeze at whatever the first render produced while the legend beside
   * them keeps updating — two views of the data disagreeing, with nothing on screen
   * saying which is current.
   *
   * Built from the FILL INPUTS only (code, value, denominator, kind, threshold), not from
   * the whole config: `label` and `footnote` are rendered by the legend and change
   * nothing about a polygon, so including them would rebuild the layer for a copy edit.
   */
  const choroplethFillSignature = useMemo(() => {
    if (!choropleth) return "";
    return [
      choropleth.kind,
      choropleth.minDenominator ?? "",
      choropleth.data
        .map((d) => `${d.code}:${d.value}:${d.denominator}`)
        .join(","),
    ].join("|");
  }, [choropleth]);



  useEffect(() => {
    if (!gmap) return;
    if (items.length === 0) return;
    // ── HEAT MODE: no pins ─────────────────────────────────────────────────────────
    // A prior PINS-mode run's own cleanup (the `return () => {...}` below) already
    // tore down whatever markers/clusterer it created, the instant `renderMode`
    // changed — React runs the PREVIOUS effect's cleanup before this new body runs,
    // regardless of what this body does. So there is nothing left to clear here; this
    // run just declines to create anything new. See the sibling heatmap effect below
    // for what heat mode renders instead.
    if (renderMode === "heat") return;

    clustererRef.current?.clearMarkers();
    nativeMarkersRef.current.forEach((m) => m.setMap(null));
    nativeMarkersRef.current = [];

    // One reusable InfoWindow drives the (instant) hover popover — styled dark via
    // the .vera-map-tip CSS on the map container (see the outer Box sx).
    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow({ disableAutoPan: true });
    }

    // Initial dot size for the CURRENT zoom (the zoom-changed effect below keeps it
    // in sync afterwards) — so a fresh render already paints the right size, no flash.
    const initialSize = dotSizeForZoom(gmap.getZoom() ?? 4);

    // Group by rounded position first, so scans sharing a spot become ONE stack dot
    // instead of N dots hidden under each other.
    // NB: a plain object, NOT `new Map()` — this module exports a component called
    // `Map`, which shadows the global, so `new Map()` here silently constructs the
    // React component instead of a hash and every .get/.set blows up at runtime.
    const groupsByKey: { [key: string]: { m: MapMarkerData; index: number }[] } = {};
    const groupOrder: string[] = [];
    items.forEach((m, index) => {
      const key = positionKey(m.lat, m.lng);
      if (!groupsByKey[key]) {
        groupsByKey[key] = [];
        groupOrder.push(key);
      }
      groupsByKey[key].push({ m, index });
    });

    nativeMarkersRef.current = groupOrder.map((groupKey) => {
      const group = groupsByKey[groupKey];
      // The stack inherits its most-alerting member's color, same rule clusters use —
      // a stack hiding a tampered scan must not read as an ordinary one.
      const lead = group.reduce((top, g) =>
        ((g.m as any).clusterPriority ?? 0) > ((top.m as any).clusterPriority ?? 0)
          ? g
          : top,
      );
      const { m, index } = group[0];
      const isStack = group.length > 1;
      // A STACK paints itself with its most-alerting member (`lead`), not with whichever
      // scan happens to be first in the array. Several scans share this one point, so a
      // clean scan sitting first would otherwise hide an invalid one underneath it and
      // the pin would read as fine. A lone dot is just its own colour.
      const fillColor = (isStack ? lead.m.fillColor : m.fillColor) || DEFAULT_PIN_COLOR;

      const markerSvg = isStack
        ? createStackDotSvg(group.length, fillColor, isDarkBasemap)
        : createCustomMarkerSvg(fillColor);
      // Stacks render a touch larger than a lone dot so the count stays legible.
      const size = isStack ? Math.round(initialSize * STACK_SIZE_PREMIUM) : initialSize;

      const marker = new google.maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        // No native title — the hover uses a dark InfoWindow (below) instead of the
        // slow, plain browser tooltip.
        title: "",
        map: enableClustering ? undefined : gmap,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(markerSvg),
          anchor: new google.maps.Point(size / 2, size / 2),
          scaledSize: new google.maps.Size(size, size),
        },
        // Paint higher-severity pins ON TOP so a tampered (red) dot is never hidden
        // beneath a valid (white) one when they sit at the same / near-same point.
        zIndex: (lead.m as any).clusterPriority ?? 0,
      });
      // Stash the member's severity so a cluster can tint itself to its most-alerting
      // member (e.g. a cluster containing a tampered scan renders red, not grey).
      marker.set("clusterPriority", (lead.m as any).clusterPriority ?? 0);
      marker.set("clusterColor", (lead.m as any).clusterColor);
      marker.set("isStack", isStack);

      if (isStack) {
        // Clicking a stack lists what's there rather than fanning the pins out. The fan
        // scattered markers away from the place they actually happened, which misreads
        // as several locations; a list keeps one pin = one place and puts the detail in
        // text, where the scans can carry their own names, times and statuses.
        marker.addListener("click", () => {
          // The hover tip is showing right now (the pointer is on the marker); leaving
          // it up would overlap the list that's about to open on the same spot.
          infoWindowRef.current?.close();
          // Second click on an open stack closes it, so the map can be cleared without
          // hunting for the X or for empty space.
          // Preferred path: hand the members to the consumer and render nothing on the
          // map. Keeps one surface showing scan detail instead of a tooltip competing
          // with the docked card below the map, which previously stayed on the PREVIOUS
          // single scan while the tooltip described a different place entirely.
          //
          // Checked BEFORE the toggle below on purpose. The toggle exists to dismiss a
          // tooltip drawn on the map; when the consumer owns the presentation there is
          // nothing here to dismiss, and falling into it would return early WITHOUT
          // telling the consumer — leaving the card showing a stack the map believes it
          // has closed. Re-clicking an open stack simply re-opens it, which is a no-op.
          if (onStackClick) {
            onStackClick(group.map((g) => g.m.item));
            openStackKeyRef.current = groupKey;
            return;
          }
          if (openStackKeyRef.current === groupKey) {
            closeStackTip();
            return;
          }
          if (!stackTipRef.current) {
            stackTipRef.current = new google.maps.InfoWindow({
              disableAutoPan: false,
              // Without a cap the window sizes to its widest row, which on a 375px
              // phone ran off the screen and truncated the timestamps. The consumer
              // sets the real content width; this is just the ceiling that keeps the
              // window inside the map card on the narrowest phone.
              maxWidth: 340,
            });
          }
          const members = group.map((g) => g.m.item);
          const html = renderStackTooltip
            ? renderStackTooltip(members)
            : `<div class="vera-map-tip">${group.length} scans here</div>`;
          stackTipRef.current.setContent(html);
          stackTipRef.current.setPosition({ lat: m.lat, lng: m.lng });
          stackTipRef.current.open({ map: gmap });
          openStackKeyRef.current = groupKey;
        });
      } else {
        marker.addListener("click", () => onMarkerClick(index));
      }

      // Instant hover popover (VERA-styled dark tooltip). A consumer can supply rich
      // markup via item.tooltipHtml (it owns escaping); otherwise we escape + show the
      // plain title string.
      const content = isStack
        ? `<div class="vera-map-tip">${group.length} scans here — click to list them</div>`
        : buildTip(m);
      if (content) {
        marker.addListener("mouseover", () => {
          if (isStack && openStackKeyRef.current === groupKey) return;
          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open({ map: gmap, anchor: marker });
        });
        marker.addListener("mouseout", () => infoWindowRef.current?.close());
      }
      return marker;
    });

    if (enableClustering) {
      clustererRef.current = new MarkerClusterer({
        markers: nativeMarkersRef.current,
        map: gmap,
        renderer: {
          render: ({ count, position, markers: clusterMarkers }) => {
            // Pick the most-alerting member so the cluster inherits its color — the alert
            // survives clustering. Ranking comes from the caller's `clusterPriority`, which
            // for scans is `getScanStatus(...).severity`: invalid (3) > suspicious link (2)
            // > stale/invalid link (1) > verified, connected and OPENED (all 0). An earlier
            // note here read "tampered > invalid > valid", which inverted the top two — an
            // opened seal is a fact about a closure tag, not a failed scan (VRG-491).
            //
            // NOTE the `p > topPriority` test below starts from 0, so a severity-0 member
            // can never tint a cluster whatever colour it carries.
            let accent: string | undefined;
            let topPriority = 0;
            (clusterMarkers ?? []).forEach((cm: any) => {
              const p = (cm.get?.("clusterPriority") as number) ?? 0;
              const c = cm.get?.("clusterColor") as string | undefined;
              if (c && p > topPriority) {
                topPriority = p;
                accent = c;
              }
            });
            const svg = clusterRenderer
              ? clusterRenderer(count, accent, isDarkBasemap)
              : createClusterSvg(count, accent, isDarkBasemap);

            // Same `STACK_SIZE_PREMIUM` a coincident-point stack applies to its own
            // marker, and for the same reason: the SVG's dot is drawn UNDERSIZED (see
            // `createClusterSvg` / `dotRadiusForPill`) precisely so that after this
            // premium it lands at lone-pin size, not larger — a cluster must not read as
            // a bigger scan, only an annotated one. Read the CURRENT zoom fresh here
            // rather than the `initialSize` this effect captured once at mount: the
            // clusterer calls `render` again on every zoom change, and that outer
            // variable would go stale the first time the user zoomed.
            const size = Math.round(dotSizeForZoom(gmap.getZoom() ?? 4) * STACK_SIZE_PREMIUM);

            return new google.maps.Marker({
              position,
              icon: {
                url:
                  "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
                anchor: new google.maps.Point(size / 2, size / 2),
                scaledSize: new google.maps.Size(size, size),
              },
            });
          },
        },
      });
    }

    return () => {
      closeStackTip();
      clustererRef.current?.clearMarkers();
      nativeMarkersRef.current.forEach((m) => m.setMap(null));
      nativeMarkersRef.current = [];
    };
    // `isDarkBasemap` is a dep because the marker icons are RASTER SVG data-URLs baked at
    // creation time — a theme flip has to rebuild them, it cannot restyle them.
  }, [gmap, items, onMarkerClick, clusterRenderer, enableClustering, onStackClick, renderMode, isDarkBasemap]);

  /**
   * ── HEAT MODE (VERA-260) ──────────────────────────────────────────────────────────
   *
   * Was `google.maps.visualization.HeatmapLayer`. Google REMOVED it in Maps JS v3.65
   * while leaving the symbol on the namespace, so it could not be feature-detected —
   * only caught, and only after it had already taken the whole map subtree down through
   * the error boundary once (a `new HeatmapLayer` throw inside an effect, uncaught,
   * blanked the map in PINS mode too). See the git history of this file for the full
   * post-mortem; it is not repeated here because it no longer describes live code.
   *
   * Replaced with deck.gl's `HeatmapLayer` (`@deck.gl/aggregation-layers`), rendered
   * through `GoogleMapsOverlay` (`@deck.gl/google-maps`) — the vendor-supported bridge
   * from a deck.gl layer set onto a `google.maps.Map` instance. Both are from vis.gl,
   * the same org that publishes `@vis.gl/react-google-maps` already in use here, and
   * this is Google's own suggested replacement for the removed layer.
   *
   * LAZY-IMPORTED, not a static import. Unlike the old `visualization` Maps-JS library
   * flag (a cheap, always-requested query param), deck.gl is a genuinely heavy WebGL
   * vendor stack (~8MB unpacked across the three packages, before their own
   * dependencies). A `renderMode="pins"` consumer — every consumer today except this
   * card's own heat toggle — must not pay to fetch it. `import(...)` is cached by the
   * module loader after the first call, so switching between pins and heat repeatedly
   * costs nothing beyond the very first switch to heat.
   *
   * ⚠️ DENSITY ONLY — NEVER WEIGHT THIS BY STATUS. A single colour gradient cannot
   * carry two independent signals (how many scans happened here, and how many of them
   * were invalid/tampered) without one masking the other — a hotspot that is entirely
   * clean and a hotspot that is mostly counterfeit would render identically if this
   * were weighted or coloured by severity. That is precisely the signal this whole
   * product exists to surface, so anomaly detection stays on PINS (status colour,
   * per-marker tooltip, stack severity) and heat stays a plain, unweighted density
   * surface: every point contributes the SAME weight regardless of what it is. Do not
   * "improve" this by passing a real `getWeight` derived from tampered/invalid points,
   * or a `colorRange` tinted by status — that is the one change to this effect that
   * would break its contract. `getWeight` is set to a constant `1` explicitly (deck.gl's
   * own default) so the decision stays visible here rather than resting on an implicit
   * default nobody re-reads this comment before "fixing".
   */
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  useEffect(() => {
    if (!gmap) return;

    if (renderMode !== "heat") {
      /*
        Leaving heat mode (or never entered it) — hide what's painted but keep the
        overlay instance (and its WebGL context) alive. Tearing down and recreating a
        GL context on every pins<->heat toggle is the expensive part of this; clearing
        its layer list is not. The instance is only released on true unmount, below.

        ⚠️ UNLESS THE OTHER DECK MODE IS TAKING OVER. `heat` and `choropleth` share ONE
        `overlayRef`, and both effects run on every `renderMode` change — so if this
        branch cleared unconditionally it would race the sibling effect for the shared
        overlay, and which one won would depend on nothing more principled than the order
        the two `useEffect` calls appear in this file. Switching pins -> choropleth would
        have worked (this effect runs first, the choropleth effect paints after it) and
        choropleth -> heat would have silently rendered NOTHING (heat paints, then the
        choropleth effect's own cleanup wipes it). Each effect declining to touch the
        overlay while the other mode is active removes the ordering dependency entirely.
      */
      if (!isDeckMode(renderMode)) {
        overlayRef.current?.setProps({ layers: [] });
        overlayRef.current?.setMap(null);
      }
      return;
    }

    let cancelled = false;

    /*
      ⚠️ COERCE AND FILTER BEFORE HANDING OFF. `geo_lat`/`geo_lng` are Postgres
      `numeric`, which node-postgres returns as STRINGS — they happen to arrive coerced
      today, but this layer should not depend on a serialisation detail two services
      away. `Number.isFinite` drops null/undefined/NaN rather than letting one bad row
      poison the whole aggregation. Pins already tolerate this via the upstream
      `lat != null` filter; heat needs its own because it consumes `items` directly.
    */
    const data = items.reduce<{ lat: number; lng: number }[]>((acc, m) => {
      const lat = Number(m.lat);
      const lng = Number(m.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        acc.push({ lat, lng });
      }
      return acc;
    }, []);

    const fail = (err: unknown) => {
      overlayRef.current?.finalize();
      overlayRef.current = null;
      // eslint-disable-next-line no-console
      console.warn(
        "Map: could not render the heat layer (deck.gl unavailable, no WebGL context, or a render error). Falling back to pins.",
        err,
      );
      onHeatUnavailable?.();
    };

    void (async () => {
      try {
        const [{ GoogleMapsOverlay: GoogleMapsOverlayCtor }, { HeatmapLayer }] =
          await Promise.all([
            import("@deck.gl/google-maps"),
            import("@deck.gl/aggregation-layers"),
          ]);
        if (cancelled || !gmap) return;

        const overlay =
          overlayRef.current ??
          new GoogleMapsOverlayCtor({
            /*
              deck.gl's own failure channel — fired for a construction-time problem (no
              WebGL2 context available: a headless/software-rendering environment) AND a
              later render-time one (both land here, which is why this is not a
              try/catch around `new` alone). A vendor layer that cannot draw must
              degrade to "this view is unavailable", never take the map down — the exact
              lesson the removed Google layer left behind.
            */
            onError: (err: Error) => fail(err),
          });

        overlayRef.current = overlay;
        overlay.setMap(gmap);
        overlay.setProps({
          layers: [
            new HeatmapLayer({
              id: "vera-scan-heatmap",
              data,
              getPosition: (d: { lat: number; lng: number }) => [d.lng, d.lat],
              getWeight: () => 1,
              radiusPixels: HEATMAP_RADIUS,
              opacity: HEATMAP_OPACITY,
              // Brand purple, low -> high. See the constant for why NOT deck.gl's
              // default yellow-to-red: red already means "invalid scan" on this map,
              // so a red hot end paints the busiest place in the colour that elsewhere
              // means the most broken one.
              colorRange: HEATMAP_COLOR_RANGE,
              // deck.gl's default is 500ms, during which a zoom keeps showing the
              // PREVIOUS aggregation stretched to the new scale before snapping — the
              // "takes a while to reload when zooming" lag. See the constant.
              debounceTimeout: HEATMAP_DEBOUNCE_MS,
              aggregation: "SUM",
            }),
          ],
        });
      } catch (err) {
        // The dynamic import failed (network), or a synchronous construction/props
        // throw not routed through deck.gl's own `onError` channel above.
        if (!cancelled) fail(err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gmap, items, renderMode, onHeatUnavailable]);

  /**
   * ── CHOROPLETH MODE (VERA-260) ────────────────────────────────────────────────────
   *
   * Countries filled by a per-country value: the correct mark for a quantity that VARIES
   * BY PLACE rather than one that HAPPENS AT a place. Pins answer "where did this scan
   * occur"; a rate has no coordinate at all, so a pin reading "3.9%" is a category error
   * — 3.9% of what, at that exact point? The three metrics this serves (countries
   * reached, invalid scan rate, repeat scan rate) are all of that shape.
   *
   * ## Renderer: the SAME deck.gl overlay heat already uses, and no new dependency
   *
   * `GoogleMapsOverlay` (`@deck.gl/google-maps`) is already the vendor-supported bridge
   * for heat mode; this adds a `GeoJsonLayer` (`@deck.gl/layers`) through it. Both are
   * lazy-imported inside this effect, so a consumer that never selects choropleth pays
   * nothing — the same contract heat's own import documents at length. The overlay
   * INSTANCE is shared with heat (see `overlayRef` above and the mutual-deference note in
   * heat's cleanup): two WebGL contexts for two modes that are never on screen together
   * would be pure waste.
   *
   * ⚠️ Deliberately NOT Google's own data-driven boundary styling. That renders country
   * fills natively with no geometry download at all, which sounds strictly better — but
   * it requires a Feature Layer configured against a Map ID in the Cloud Console, per
   * environment, by hand. This project has not done that and cannot do it from code, so
   * the feature would work in whichever environment someone remembered to configure and
   * silently render nothing everywhere else.
   *
   * ## The geometry is lazy-imported too, and it is 202KB
   *
   * `./countries/world-countries.geo.json` — Natural Earth admin-0, public domain, 240
   * features, generated by `context/scripts/build-country-geojson.mjs` (which documents
   * why it merges two resolutions rather than taking either whole). It is fetched in the
   * same `Promise.all` as deck.gl and cached by the module loader afterwards, so
   * switching modes repeatedly costs nothing after the first time.
   *
   * ## What the fill does NOT do
   *
   * A country absent from `choropleth.data` is left UNPAINTED rather than painted at the
   * bottom of the scale. "No scans here" and "the fewest scans of anywhere we reached"
   * are different facts, and a choropleth that conflates them tells a brand it has
   * covered a market it has never touched. Everything painted carries a stroke
   * (`CHOROPLETH_STROKE_*`) so the distinction survives even where the darkest band sits
   * close to the basemap.
   */
  const choroplethScale = useMemo(
    () =>
      choropleth
        ? buildChoroplethScale(
            choropleth.data,
            choropleth.kind,
            choropleth.minDenominator,
          )
        : null,
    [choropleth],
  );

  useEffect(() => {
    if (!gmap) return;

    if (renderMode !== "choropleth") {
      // See the mirror of this in heat's cleanup — neither deck mode touches the shared
      // overlay while the other one owns it.
      if (!isDeckMode(renderMode)) {
        overlayRef.current?.setProps({ layers: [] });
        overlayRef.current?.setMap(null);
      }
      return;
    }

    // The mode is selected but the consumer has not supplied data yet (first load).
    // Paint nothing rather than an empty world, and do NOT report the mode unavailable —
    // nothing has failed, the data is simply in flight.
    if (!choropleth || !choroplethScale) return;

    let cancelled = false;

    // Code -> datum, so the layer's per-feature accessor is a hash lookup rather than a
    // linear scan of the row set for each of 240 polygons on every re-render.
    const byCode: { [code: string]: MapChoroplethDatum } = {};
    choropleth.data.forEach((d) => {
      if (d && typeof d.code === "string") byCode[d.code] = d;
    });

    const fail = (err: unknown) => {
      overlayRef.current?.finalize();
      overlayRef.current = null;
      // eslint-disable-next-line no-console
      console.warn(
        "Map: could not render the choropleth layer (deck.gl unavailable, no WebGL context, the country geometry failed to load, or a render error).",
        err,
      );
      onChoroplethUnavailable?.();
    };

    void (async () => {
      try {
        const [
          { GoogleMapsOverlay: GoogleMapsOverlayCtor },
          { GeoJsonLayer },
          geoModule,
        ] = await Promise.all([
          import("@deck.gl/google-maps"),
          import("@deck.gl/layers"),
          import("./countries/world-countries.geo.json"),
        ]);
        if (cancelled || !gmap) return;

        /*
          A bundler hands a dynamically-imported JSON back as a module NAMESPACE with the
          document on `.default`; TypeScript, with `resolveJsonModule`, types the same
          import as the document ITSELF. Both are correct about their own layer and they
          disagree, so read through whichever one is present rather than asserting a
          shape that is only true for one toolchain.
        */
        const geojson =
          ((geoModule as any).default ?? geoModule) as CountryGeoJson;

        const overlay =
          overlayRef.current ??
          new GoogleMapsOverlayCtor({
            // deck.gl's own failure channel, for a construction-time problem (no WebGL2
            // context) and a later render-time one alike. A vendor layer that cannot draw
            // must degrade to "this view is unavailable", never take the map down.
            onError: (err: Error) => fail(err),
          });

        overlayRef.current = overlay;
        overlay.setMap(gmap);

        if (!choroplethTipRef.current) {
          choroplethTipRef.current = new google.maps.InfoWindow({
            disableAutoPan: true,
          });
        }

        overlay.setProps({
          layers: [
            new GeoJsonLayer({
              id: "vera-country-choropleth",
              data: geojson as any,
              filled: true,
              stroked: true,
              // Screen-space so a country's outline is the same weight at every zoom;
              // in the default metre units a 1-unit line is invisible at world view.
              lineWidthUnits: "pixels",
              getLineWidth: 1,
              // Flat fills — this is a thematic surface, not terrain. Extrusion would
              // add a third visual channel carrying no additional information and would
              // occlude the countries behind whichever one is tallest.
              extruded: false,
              getFillColor: (f: any) => {
                const d = byCode[f?.properties?.a3];
                // Transparent, NOT the bottom band — see the header on why "no scans"
                // and "fewest scans" must not render the same.
                if (!d) return [0, 0, 0, 0];
                const c = colorFor(
                  d,
                  choropleth.kind,
                  choroplethScale,
                  choropleth.minDenominator,
                );
                return [
                  c[0],
                  c[1],
                  c[2],
                  isInsufficient(d, choropleth.kind, choropleth.minDenominator)
                    ? CHOROPLETH_INSUFFICIENT_ALPHA
                    : CHOROPLETH_FILL_ALPHA,
                ];
              },
              getLineColor: (f: any) =>
                byCode[f?.properties?.a3]
                  ? [
                      CHOROPLETH_STROKE_COLOR[0],
                      CHOROPLETH_STROKE_COLOR[1],
                      CHOROPLETH_STROKE_COLOR[2],
                      CHOROPLETH_STROKE_ALPHA,
                    ]
                  : [0, 0, 0, 0],
              /*
                ⚠️ REQUIRED. deck.gl caches per-feature accessor output and will NOT
                re-evaluate it when a value the accessor closes over changes, because it
                has no way to know that it did — `data` is the same 240 features either
                way. Without this the map keeps last render's colours after a filter
                change: the legend updates, the fills do not, and the two silently
                describe different data.
              */
              updateTriggers: {
                getFillColor: choroplethFillSignature,
                getLineColor: choroplethFillSignature,
              },
              pickable: true,
              autoHighlight: true,
              highlightColor: [255, 255, 255, 40],
              onHover: (info: any) => {
                const tip = choroplethTipRef.current;
                if (!tip) return;
                const d = byCode[info?.object?.properties?.a3];
                if (!d || !info?.coordinate) {
                  tip.close();
                  hoveredCountryRef.current = undefined;
                  return;
                }
                /*
                  `onHover` fires on every mousemove, so the CONTENT is rebuilt only when
                  the country actually changes — `setContent` on a Google InfoWindow tears
                  down and re-renders its DOM, which at mousemove rate is both wasted work
                  and a visible flicker. The POSITION still updates every event, so the
                  tooltip trails the cursor across a large country the way a hover tip
                  should.
                */
                if (hoveredCountryRef.current !== d.code) {
                  tip.setContent(buildChoroplethTip(d, choropleth));
                  hoveredCountryRef.current = d.code;
                }
                tip.setPosition({
                  lat: info.coordinate[1],
                  lng: info.coordinate[0],
                });
                tip.open({ map: gmap });
              },
            }),
          ],
        });
      } catch (err) {
        // The dynamic import failed (network), or a synchronous construction/props throw
        // not routed through deck.gl's own `onError` channel above.
        if (!cancelled) fail(err);
      }
    })();

    return () => {
      cancelled = true;
      choroplethTipRef.current?.close();
      // Forget which country is described, or the next hover on the SAME country after a
      // filter change would skip the content rebuild and show the previous figures.
      hoveredCountryRef.current = undefined;
    };
  }, [
    gmap,
    renderMode,
    choropleth,
    choroplethScale,
    choroplethFillSignature,
    onChoroplethUnavailable,
  ]);

  // True unmount only — the effect above already re-points `.setMap(null)` on every
  // mode change; this releases the WebGL context so it doesn't outlive the component.
  useEffect(() => {
    return () => {
      overlayRef.current?.finalize();
      overlayRef.current = null;
      choroplethTipRef.current?.close();
      choroplethTipRef.current = null;
    };
  }, []);

  // Zoom-responsive dot size — resize every native marker's icon as the user zooms
  // so dots grow when zoomed in and shrink at world-view. Re-subscribes whenever the
  // marker set is rebuilt (deps: gmap, items).
  useEffect(() => {
    if (!gmap) return;
    const applySize = () => {
      const size = dotSizeForZoom(gmap.getZoom() ?? 4);
      nativeMarkersRef.current.forEach((marker) => {
        const icon = marker.getIcon();
        if (icon && typeof icon === "object") {
          const prev = icon as google.maps.Icon;
          // Google's legacy Marker short-circuits setIcon when the icon `url` is
          // unchanged — it ignores a new scaledSize. Tag the url with the size so it
          // actually re-renders at the new zoom.
          const baseUrl = String(prev.url ?? "").split("#")[0];
          // Stacks keep their size premium at every zoom, or the count becomes
          // unreadable the moment you zoom out.
          const s = marker.get("isStack")
            ? Math.round(size * STACK_SIZE_PREMIUM)
            : size;
          marker.setIcon({
            ...prev,
            url: `${baseUrl}#s${s}`,
            scaledSize: new google.maps.Size(s, s),
            anchor: new google.maps.Point(s / 2, s / 2),
          });
        }
      });
    };
    applySize();
    const listener = gmap.addListener("zoom_changed", applySize);
    return () => google.maps.event.removeListener(listener);
  }, [gmap, items]);

  // The stack list is anchored to one point at one zoom; on zoom the clusterer may
  // regroup that point away entirely, leaving a popover pointing at nothing. Close it on
  // zoom, and on empty-map click — the same gesture that clears a selection.
  useEffect(() => {
    if (!gmap) return;
    const onZoom = gmap.addListener("zoom_changed", () => closeStackTip());
    const onClick = gmap.addListener("click", () => closeStackTip());
    return () => {
      google.maps.event.removeListener(onZoom);
      google.maps.event.removeListener(onClick);
    };
  }, [gmap, closeStackTip]);

  // Click empty map space (NOT a marker — Google suppresses the map 'click' when a
  // marker is clicked) → clear the active selection.
  useEffect(() => {
    if (!gmap || !onDeselect) return;
    const listener = gmap.addListener("click", () => onDeselect());
    return () => google.maps.event.removeListener(listener);
  }, [gmap, onDeselect]);

  /*
    Zoom-out floor: stop the world repeating horizontally.

    Google tiles copies of the world whenever the viewport is wider than one world at
    the current zoom. One world is `WORLD_TILE_PX * 2^zoom` px, so the zoom at which a
    single copy exactly spans the container is `log2(width / WORLD_TILE_PX)` — that is
    the floor, and it is a function of CONTAINER WIDTH, which is why this cannot be a
    constant prop (see the note at the `<GoogleMap>` call site).

    Deliberately floor-to-fit rather than leaving a sliver of wrap: at exactly this zoom
    the whole world is still visible, so nothing is taken away — you simply cannot pull
    back into a second copy. Clamped at `MIN_WORLD_ZOOM` so a pathologically narrow
    container can't drive the floor below the usable range.
  */
  // Hoisted to a stable callback (was inline in the effect below) so the relayoutNonce
  // effect further down can call the SAME computation rather than a second copy of it —
  // a container-width change is a container-width change regardless of which mechanism
  // noticed it.
  // VRG-699: the floor last applied, so `frameWorld` can tell "still showing the whole
  // world" (re-frame on resize) from "the viewer zoomed in" (leave the camera alone).
  const lastFloorRef = useRef<number | null>(null);
  const applyFloor = useCallback(() => {
    if (!gmap) return;
    const width = gmap.getDiv()?.clientWidth ?? 0;
    if (!width) return;
    // log2 via natural log: this package's tsconfig targets pre-ES2015, so `Math.log2`
    // does not typecheck here even though it runs fine. Same value, no lib dependency.
    // A `frameWorld` map may go below MIN_WORLD_ZOOM (down to 0, one 256px world): on a
    // phone-width card the clamp at 1 would make the world 512px wide and crop it.
    const floor = Math.max(
      frameWorld ? 0 : MIN_WORLD_ZOOM,
      Number((Math.log(width / WORLD_TILE_PX) / Math.LN2).toFixed(2)),
    );
    const prevFloor = lastFloorRef.current;
    lastFloorRef.current = floor;
    // `isFractionalZoomEnabled` set explicitly: the one-world zoom is almost never an
    // integer, and Google's default for the option differs by map type (on for vector,
    // off for raster). Snapped, the world would either repeat or crop.
    gmap.setOptions(
      frameWorld ? { minZoom: floor, isFractionalZoomEnabled: true } : { minZoom: floor },
    );
    const z = gmap.getZoom();
    if (
      frameWorld &&
      (prevFloor === null || typeof z !== "number" || Math.abs(z - prevFloor) < 0.05)
    ) {
      gmap.setZoom(floor);
      gmap.setCenter(WORLD_FRAME_CENTER);
      return;
    }
    // Already zoomed out past the new floor (container got narrower, or the floor
    // rose): pull back in, or Google leaves the repeated copies on screen.
    if (typeof z === "number" && z < floor) gmap.setZoom(floor);
  }, [gmap, frameWorld]);

  /*
    VRG-699 — a `frameWorld` map keeps the viewport inside the one world BY HAND, in place
    of `strictBounds`. Measured: with `strictBounds: true` Google refuses any zoom below 1
    (setZoom(0.76) reads back 1), so a map narrower than 512px could never show the whole
    world; at 1024px wide the live map is ~414px and lost Alaska and New Zealand. Without
    `strictBounds` the zoom is honoured but the viewport may leave the world, so this
    clamps the centre so the edges stay within ±85.05° (the Mercator square, y = ±π) and
    ±180°. Converges in one step: the clamped centre is already in range.
  */
  useEffect(() => {
    if (!gmap || !frameWorld) return;
    const clampToWorld = () => {
      const c = gmap.getCenter();
      const z = gmap.getZoom();
      const div = gmap.getDiv();
      if (!c || typeof z !== "number" || !div?.clientWidth) return;
      const worldPx = WORLD_TILE_PX * Math.pow(2, z);
      const halfY = (div.clientHeight / 2) * ((2 * Math.PI) / worldPx);
      const halfLng = (div.clientWidth / 2) * (360 / worldPx);
      const yMax = Math.max(0, Math.PI - halfY);
      const lngMax = Math.max(0, 180 - halfLng);
      const y = mercatorY(c.lat());
      const yc = Math.max(-yMax, Math.min(yMax, y));
      const lngc = Math.max(-lngMax, Math.min(lngMax, c.lng()));
      if (Math.abs(yc - y) > 1e-9 || Math.abs(lngc - c.lng()) > 1e-9) {
        gmap.setCenter({ lat: latFromMercatorY(yc), lng: lngc });
      }
    };
    const listener = gmap.addListener("center_changed", clampToWorld);
    return () => google.maps.event.removeListener(listener);
  }, [gmap, frameWorld]);

  useEffect(() => {
    if (!gmap) return;

    applyFloor();

    // BOTH observers on purpose. ResizeObserver is the correct one — this map is
    // resized by a draggable split and by card maximise, neither of which fires a
    // window resize. window.resize is the fallback for environments where
    // ResizeObserver does not fire (the CCD preview pane is one), so the floor is
    // still applied there rather than silently regressing to "no floor at all".
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(applyFloor) : null;
    const div = gmap.getDiv();
    if (ro && div) ro.observe(div);
    window.addEventListener("resize", applyFloor);

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", applyFloor);
    };
  }, [gmap, applyFloor]);

  /*
    ── EXPLICIT RE-LAYOUT SIGNAL (`relayoutNonce`) ────────────────────────────────────

    See `MapProps.relayoutNonce` for the contract. This is the observer-INDEPENDENT
    counterpart to the ResizeObserver above: for a consumer that changes this map's own
    container width on purpose and cannot trust an observer (Google's own internal
    auto-resize detection is ALSO an observer, and does not fire in every embedded
    preview surface) to notice it.

    `google.maps.event.trigger(gmap, "resize")` is the documented manual mechanism for
    telling Google "your container changed size, re-read it and re-tile" — it predates
    ResizeObserver support in the JS API for exactly this class of gap. It re-reads the
    DOM synchronously, so by the time this call returns the map already believes it has
    the new box; nothing here is waiting on a callback that might not fire.

    A plain `trigger('resize')` re-centres the map on its OWN div's center, which is a
    real camera jump if the div's center moved (e.g. the map got narrower from the left
    edge). Capturing and restoring `getCenter()` around the call is what keeps the
    camera still — this signal is "the box changed", not "look somewhere else".
  */
  const prevRelayoutNonceRef = useRef(relayoutNonce);
  useEffect(() => {
    if (!gmap) return;
    if (relayoutNonce === undefined) return;
    // Only a genuine CHANGE — not the value this instance mounted with, which already
    // describes the box the map tiled for at mount. Mirrors the "swallow the first
    // measurement" shape the width-observer effects in this file already use.
    if (prevRelayoutNonceRef.current === relayoutNonce) return;
    prevRelayoutNonceRef.current = relayoutNonce;

    const center = gmap.getCenter();
    google.maps.event.trigger(gmap, "resize");
    if (center) gmap.setCenter(center);

    // The zoom-out floor is a function of container WIDTH (see `applyFloor` above) — a
    // width change is exactly what this signal represents, so re-apply it immediately
    // rather than waiting for the ResizeObserver's own 180ms-class debounce upstream
    // (in the consumer) to catch up.
    applyFloor();
  }, [gmap, relayoutNonce, applyFloor]);

  // Journey line: connect `path` in order so the SEQUENCE of a single subject's points
  // is visible on the map, not just in the list beside it. Redrawn whenever the path
  // changes and torn down on unmount/disable so toggling never leaves a stale line.
  //
  // Geodesic so a leg follows the great-circle route the way real travel does (and so a
  // London -> Beijing hop doesn't render as a straight line across the projection).
  // Arrow symbols carry the DIRECTION — without them a line shows the route but not
  // which way it ran, which is the whole point of drawing it.
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  useEffect(() => {
    if (!gmap) return;

    polylineRef.current?.setMap(null);
    polylineRef.current = null;

    if (!path || path.length < 2) return;

    polylineRef.current = new google.maps.Polyline({
      path,
      map: gmap,
      geodesic: true,
      strokeColor: pathColor ?? DEFAULT_PIN_COLOR,
      strokeOpacity: 0.9,
      strokeWeight: 2,
      // Below the markers — the line is context for the pins, never covering them.
      zIndex: 1,
      icons: [
        {
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 2.6,
            strokeColor: pathColor ?? DEFAULT_PIN_COLOR,
            fillColor: pathColor ?? DEFAULT_PIN_COLOR,
            fillOpacity: 1,
            strokeWeight: 1,
          },
          // Repeat rather than one mid-arrow, so direction stays readable on a long leg.
          offset: "0",
          repeat: "120px",
        },
      ],
    });

    return () => {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
    };
  }, [gmap, path, pathColor]);

  // Fit ALL markers into view. For fitToItems maps this re-runs whenever the item SET
  // changes (e.g. a filter is applied) so the viewport zooms to the filtered scans —
  // keyed on a signature of the items so panning/zooming (items unchanged) never re-fits.
  // Non-fitToItems maps keep the original once-only zoom-to-last behavior.
  const fittedSigRef = useRef<string>("");
  // `fitSignature` mode's OWN tracking ref — see the prop doc. `null` (not `""`) so the
  // very first real signature, even an empty string, still counts as "changed" and fits.
  const fittedForFitSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!gmap) return;
    if (items.length === 0) return;
    // A `frameWorld` map owns its camera: the whole world is the frame, so neither a
    // fit to the items nor the zoom-to-last default may move it.
    if (frameWorld) return;

    if (fitToItems) {
      // Shared by both branches below — the actual camera move, given the items are
      // already known to be worth fitting to.
      const runFit = () => {
        // Distinct coordinates: many scans in one city (e.g. all "Paris") collapse to a
        // single point, so we don't fitBounds a zero-area box — that over-zooms to street
        // level on a featureless tile. Only fit when there are ≥2 genuinely different spots.
        const uniq = Array.from(new Set(items.map((m) => `${m.lat},${m.lng}`)));
        if (uniq.length > 1) {
          const bounds = new google.maps.LatLngBounds();
          items.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
          gmap.fitBounds(bounds, 56);
          // Cap the zoom so a tight cluster of nearby points doesn't blow past city level.
          google.maps.event.addListenerOnce(gmap, "idle", () => {
            const z = gmap.getZoom();
            if (typeof z === "number" && z > 13) gmap.setZoom(13);
          });
        } else {
          // All scans at one place — center it at city zoom.
          gmap.panTo({ lat: items[0].lat, lng: items[0].lng });
          gmap.setZoom(11);
        }
      };

      if (fitSignature !== undefined) {
        // ── VRG-480 draw-to-filter, take two: an explicit trigger, not a race ──────────
        // The original mechanism fit on every item-SET-signature change and asked a
        // CONSUMER to arm a `suppressFit` flag on the one render it knew the upcoming
        // item-set change shouldn't move the camera, for the MAP to consume on the
        // later render where the new items actually land. That depends on the consumer's
        // "arm" effect and this effect's "consume" run landing in an order React does not
        // contract for two unrelated components — it held until a commit shape existed
        // where it didn't (draw-clear's `area` reset and the requery's `points` update
        // landing in the same commit raced the flag instead of ordering it).
        //
        // `fitSignature` removes the race by removing the two-sided handoff: there is
        // only one signal, and this effect reads it and `items` together, in the same
        // render, every time. A consumer builds the string from exactly the inputs that
        // SHOULD re-frame the camera (date range, owner, product, ...) and leaves out the
        // ones that shouldn't (an on-map drawn area) — so an item-set change caused by
        // drawing or clearing an area literally cannot fit, because nothing it does moves
        // this string. No flag, no ordering dependency, nothing to consume.
        if (fittedForFitSignatureRef.current === fitSignature) return;
        // Recorded BEFORE the veto below, for the same reason the legacy path records its
        // signature before checking `suppressFit`: a suppressed update still happened, and
        // the next genuine change must compare against it or it looks like a no-op and
        // silently loses its own fit.
        fittedForFitSignatureRef.current = fitSignature;
        /*
          ⚠️ `suppressFit` IS HONOURED HERE TOO. It used to be checked only in the legacy
          path below, which this branch returns before ever reaching — so a consumer in
          `fitSignature` mode could pass it and be silently ignored.

          That gap is why "setting a draw still zooms in": the signature check alone assumes
          this component instance SURVIVES the filter change, because the "already fitted"
          ref lives in the instance. Anything that recreates the map — a remount from any
          source — resets that ref to `null`, so the very next run treats the current
          signature as new and re-frames around whatever pins are now visible. The signature
          prevents a re-fit; it cannot prevent a FIRST fit.

          `suppressFit` is the standing veto that survives that: a consumer sets it while an
          on-map area is applied, and then no instance, however fresh, re-frames around the
          filtered subset. The user's words: "prevent this action when drawing."
        */
        if (suppressFit) return;
        runFit();
        return;
      }

      // Legacy path — unchanged for every consumer not yet passing `fitSignature`.
      const sig = items.map((m) => `${m.lat},${m.lng}`).join("|");
      if (sig === fittedSigRef.current) return;
      // Recorded BEFORE the `suppressFit` check below, always — a suppressed update still
      // changed the item set, and the next genuine change has to compare against where the
      // items actually are now, not against whichever signature we last MOVED the camera
      // for. Skipping this line on a suppressed update would make the following real
      // change look like a no-op signature match and silently drop its fit too.
      fittedSigRef.current = sig;
      if (suppressFit) return;
      runFit();
    } else if (!hasZoomedToLastRef.current) {
      const last = items[items.length - 1];
      gmap.panTo({ lat: last.lat, lng: last.lng });
      gmap.setZoom(13);
      hasZoomedToLastRef.current = true;
    }
  }, [gmap, items, fitToItems, suppressFit, fitSignature, frameWorld]);

  // Pan to the active item when it changes (user clicked a list row or marker),
  // zooming in enough to actually see it. Skips the initial (undefined) value.
  useEffect(() => {
    if (!gmap || !activeItemId) return;
    if (prevActiveRef.current === activeItemId) return;
    prevActiveRef.current = activeItemId;
    const found = items.find((m) => String(m.item.id) === String(activeItemId));
    if (found) {
      gmap.panTo({ lat: found.lat, lng: found.lng });
      gmap.setZoom(Math.max(gmap.getZoom() ?? 0, 11));
    }
  }, [gmap, activeItemId, items]);

  // Selected marker: keep its tooltip open + emit a radar pulse in the dot's own color
  // (Vergence ember-dot style). Both are torn down when the selection changes/clears.
  useEffect(() => {
    if (!gmap) return;
    // Only rebuild when the SELECTION actually changes. The map re-renders frequently;
    // tearing the pulse + tooltip down and up on every render would flicker them closed
    // (the InfoWindow renders async, so a same-frame re-run closes it before it paints).
    if (shownActiveRef.current === activeItemId) return;

    // Tear down the previously-shown selection (if any) before (re)building.
    pulseRef.current?.setMap(null);
    pulseRef.current = null;
    activeTipRef.current?.close();

    /*
      HEAT MODE DRAWS NO PINS, SO THERE IS NOTHING TO PULSE OR ANNOTATE.

      This effect is the one place that assumes the selected item has a MARK on the map: it
      builds a radar-pulse overlay at the item's coordinates and opens a tooltip over it.
      Left running in heat mode it produced a pulse ring and an InfoWindow floating over a
      density surface with no pin beneath them — pointing at nothing.

      It is also the riskiest effect to leave running there. It constructs a
      `google.maps.LatLng` directly (below), which THROWS on a non-finite argument rather
      than degrading — the same hazard that took the whole map down from the heat layer —
      and it does so on a code path that heat mode has no use for at all. Skipping it in
      heat removes both the visual nonsense and the exposure.

      `shownActiveRef` is deliberately left UNCOMMITTED: switching back to pins must rebuild
      the selection, and committing here would make this effect believe it had already drawn
      one.
    */
    if (renderMode === "heat") return;

    if (!activeItemId) {
      shownActiveRef.current = undefined;
      return;
    }

    const idx = items.findIndex((m) => String(m.item.id) === String(activeItemId));
    // Item not in the plotted set yet — leave shownActiveRef uncommitted so we retry on
    // the next render (the map finishes mounting async).
    if (idx < 0) return;
    const found = items[idx];

    // The pulse takes the colour of the most-alerting scan AT THIS POINT, not of the
    // one that happens to be selected. On a stack the card steps through members one at
    // a time, so selecting the clean scan of a pair would otherwise pulse calm purple
    // over a spot that also holds an invalid read — the alarm would disappear depending
    // on which sibling you were looking at. Lone pins are unaffected (they are their own
    // worst member).
    const activeKey = positionKey(found.lat, found.lng);
    const worstAtPoint = items
      .filter((m) => positionKey(m.lat, m.lng) === activeKey)
      .reduce(
        (top, m) =>
          ((m as any).clusterPriority ?? 0) > ((top as any).clusterPriority ?? 0) ? m : top,
        found,
      );

    /*
      Coerce and check before constructing, for the same reason the heat layer does:
      `new google.maps.LatLng(...)` THROWS on a non-finite argument, and a throw inside an
      effect takes the map subtree down through the error boundary — which presents as a
      blank map, in every mode, with nothing on screen naming the cause.

      A marker can be built from a loose `{lat, lng}` object that `LatLng` itself would
      reject, so a selection can be plottable as a PIN and still be fatal here. Coordinates
      also originate from Postgres `numeric` columns, which node-postgres returns as strings.
      If this one is unusable, skip the decoration rather than lose the map: the pin is
      already drawn and the list row is already highlighted.
    */
    const activeLat = Number(found.lat);
    const activeLng = Number(found.lng);
    if (!Number.isFinite(activeLat) || !Number.isFinite(activeLng)) return;

    const overlay = makePulseOverlay(
      new google.maps.LatLng(activeLat, activeLng),
      worstAtPoint.fillColor || found.fillColor || DEFAULT_PIN_COLOR,
    );
    overlay.setMap(gmap);
    pulseRef.current = overlay;

    // Open at the coordinates (not anchored to the marker — with clustering the marker's
    // own `map` can be undefined, which suppresses an anchored InfoWindow).
    const content = buildTip(found);
    if (content) {
      if (!activeTipRef.current) {
        activeTipRef.current = new google.maps.InfoWindow({ disableAutoPan: true });
      }
      activeTipRef.current.setContent(content);
      activeTipRef.current.setPosition({ lat: found.lat, lng: found.lng });
      activeTipRef.current.open({ map: gmap });
    }

    // Commit ONLY on success so re-renders don't churn but a not-yet-ready click retries.
    shownActiveRef.current = activeItemId;
    // `renderMode` is REQUIRED here, not incidental: the heat guard above reads it, so
    // without it in the deps this effect keeps a stale mode and never re-runs when the
    // user switches — leaving a pulse and tooltip stranded over a density surface, or
    // failing to rebuild the selection on the way back to pins.
  }, [gmap, activeItemId, items, renderMode]);

  return null;
};

// VERA-styled zoom + map-type controls, overlaid bottom-right (replaces Google's
// default zoom UI). Must render inside <GoogleMap> to reach the map via useMap().
const LAYER_OPTIONS: { id: string; label: string }[] = [
  { id: "roadmap", label: "Map" },
  { id: "hybrid", label: "Satellite" },
  { id: "terrain", label: "Terrain" },
];

const MapZoomControls: React.FC<{
  mapType: string;
  onSelectLayer: (id: string) => void;
  /** Where to anchor the control stack. Moved off the bottom when a bottom overlay
   *  would sit on top of it. */
  position?: (typeof ControlPosition)[keyof typeof ControlPosition];
}> = ({ mapType, onSelectLayer, position }) => {
  const map = useMap();
  const [layersOpen, setLayersOpen] = useState(false);
  const theme = useTheme();
  // `noSsr` so the first paint already knows the viewport — otherwise the zoom buttons
  // flash in on mobile before being removed.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  // When the stack is anchored to the TOP of the map, the mobile layer options must
  // open DOWNWARD — opening upward from a top-anchored button puts them off-map.
  const anchoredTop = position === ControlPosition.RIGHT_TOP;
  if (!map) return null;
  // Mobile keeps neither control (pinch replaces zoom; layers is desktop-only), so the
  // whole stack goes rather than rendering an empty container over the map.
  if (isMobile) return null;

  const zoomBy = (delta: number) =>
    map.setZoom(Math.max(1, (map.getZoom() ?? 10) + delta));

  const btnSx = (theme: any) => ({
    width: 36,
    height: 36,
    // 10px matches every other button in the apps (Filter/Sort/tabs). The previous
    // `borderRadius: 2` resolved to 16px, so the map controls were visibly rounder
    // than the controls sitting right above them.
    borderRadius: "10px",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.primary?.[100] ?? theme.palette.divider}`,
    color: theme.palette.text.secondary,
    boxShadow: theme.shadows[1],
    "&:hover": {
      // Keep the SOLID paper background on hover (action.hover is translucent, which
      // let the map show through). Hover feedback comes from the border + text only.
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.brand?.[600] ?? theme.palette.text.secondary,
      color: theme.palette.text.primary,
    },
  });

  return (
    <MapControl position={position ?? ControlPosition.RIGHT_BOTTOM}>
      <Stack spacing={1} sx={{ m: 2, maxWidth: "100%" }} alignItems="flex-end">
        {/* Zoom buttons are desktop-only: pinch-to-zoom is universal on touch, so
            dedicated +/- controls are redundant there and just eat map area (Google
            Maps' own mobile web drops them too). */}
        {!isMobile && (
          <>
            <IconButton aria-label="Zoom in" sx={btnSx} onClick={() => zoomBy(1)}>
              <Plus size={18} strokeWidth={1.5} />
            </IconButton>
            <IconButton aria-label="Zoom out" sx={btnSx} onClick={() => zoomBy(-1)}>
              <Minus size={18} strokeWidth={1.5} />
            </IconButton>
          </>
        )}

        {/* Layer picker — folds out to the LEFT revealing the map-type options.
            Desktop only (VERA-476): Satellite/Terrain is a desktop-analysis affordance,
            and on a phone the three options are wider than the map card, so the fold-out
            overflowed the screen. Mobile gets the default Map view, full stop. */}
        {!isMobile && (
        <Box
          onMouseEnter={isMobile ? undefined : () => setLayersOpen(true)}
          onMouseLeave={isMobile ? undefined : () => setLayersOpen(false)}
          sx={{
            display: "flex",
            // On mobile the options stack UPWARD above the button instead of folding
            // out sideways: the three options are ~240px wide, which cannot fit beside
            // the button on a 375px screen. Google's control container is not width-
            // bounded, so a horizontal fold-out silently overflowed the map instead of
            // clipping — Satellite/Terrain rendered past the right edge of the screen.
            flexDirection: isMobile
              ? anchoredTop
                ? "column-reverse"
                : "column"
              : "row",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "flex-end",
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems="flex-end"
            sx={{
              overflow: "hidden",
              ...(isMobile
                ? {
                    maxHeight: layersOpen ? 160 : 0,
                    [anchoredTop ? "mt" : "mb"]: layersOpen ? 1 : 0,
                    transition:
                      "max-height .25s ease, opacity .2s ease, margin .25s ease",
                  }
                : {
                    maxWidth: layersOpen ? 360 : 0,
                    mr: layersOpen ? 1 : 0,
                    transition:
                      "max-width .25s ease, opacity .2s ease, margin-right .25s ease",
                  }),
              opacity: layersOpen ? 1 : 0,
              flexShrink: 0,
            }}
          >
            {LAYER_OPTIONS.map((opt) => {
              const active = mapType === opt.id;
              return (
                <Box
                  key={opt.id}
                  component="button"
                  type="button"
                  aria-pressed={active}
                  onClick={() => onSelectLayer(opt.id)}
                  sx={(theme: any) => ({
                    height: 36,
                    px: 2.5,
                    whiteSpace: "nowrap",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: "inherit",
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${
                      active
                        ? theme.palette.brand?.[600] ?? theme.palette.text.primary
                        : theme.palette.primary?.[100] ?? theme.palette.divider
                    }`,
                    color: active
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                    boxShadow: theme.shadows[1],
                    "&:hover": {
                      color: theme.palette.text.primary,
                      borderColor:
                        theme.palette.brand?.[600] ?? theme.palette.text.secondary,
                    },
                  })}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Stack>

          <IconButton
            aria-label="Map layers"
            aria-expanded={layersOpen}
            sx={btnSx}
            onClick={isMobile ? () => setLayersOpen(open => !open) : undefined}
          >
            <Layers size={18} strokeWidth={1.5} />
          </IconButton>
        </Box>
        )}
      </Stack>
    </MapControl>
  );
};

export const Map: React.FC<MapProps> = ({
  items,
  activeItemId,
  apiKey,
  center,
  defaultZoom = DEFAULT_MAP_CONFIG.defaultZoom,
  height = DEFAULT_MAP_CONFIG.height,
  mapStyle = DEFAULT_MAP_CONFIG.mapStyle,
  customStyles,
  mapId,
  mapTypeId,
  enableClustering = DEFAULT_MAP_CONFIG.enableClustering,
  clusterRenderer,
  onMarkerClick,
  onDeselect,
  isLoading = false,
  showLoadingState = DEFAULT_MAP_CONFIG.showLoadingState,
  showErrorState = DEFAULT_MAP_CONFIG.showErrorState,
  errorMessage = DEFAULT_MAP_CONFIG.errorMessage,
  emptyMessage = DEFAULT_MAP_CONFIG.emptyMessage,
  renderOverlay,
  renderStackTooltip,
  onStackClick,
  overlayPosition = "bottom-left",
  path,
  pathColor,
  drawMode = "off",
  drawnRing = null,
  onDrawComplete,
  onDrawCancel,
  drawColor,
  autoZoomToLast = false,
  fitToItems = false,
  suppressFit = false,
  fitSignature,
  renderMode = "pins",
  choropleth,
  onChoroplethUnavailable,
  onHeatUnavailable,
  disableDefaultUI = DEFAULT_MAP_CONFIG.disableDefaultUI,
  zoomControl = DEFAULT_MAP_CONFIG.zoomControl,
  gestureHandling = DEFAULT_MAP_CONFIG.gestureHandling,
  relayoutNonce,
  pulseItems = false,
  frameWorld = false,
}) => {
  /**
   * NOTHING IS SELECTED UNTIL SOMETHING IS CLICKED. `-1`, not `0`.
   *
   * ⚠️ This was `useState<number>(0)`, which made `validItems[0]` active FROM MOUNT — so a
   * freshly-loaded map showed a pulse ring and an open InfoWindow over its first scan that
   * no one had clicked. Reported directly: "the map shows a pin open (tooltip present) when
   * none is clicked".
   *
   * It was not only visual noise. `activeItemId` below resolves to `validItems[activeIndex]`,
   * so a phantom selection gave the pan-to-active effect a TARGET — and that is what the
   * camera snapped to whenever the map remounted, which is why the draw-flash bug appeared
   * to "jump back to New York": New York was simply whatever sat at index 0. Two reports,
   * one root.
   *
   * `-1` is the sentinel the surrounding code was already written for — the overlay renders
   * behind `activeIndex >= 0` (below), and stepping (`activeIndex + delta + n) % n`) from
   * `-1` with `delta: +1` lands on `0`, so "next" from no-selection is still the first item.
   */
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  // Controlled map type (roadmap / hybrid / terrain) so the layer picker's selection
  // sticks — @vis.gl re-applies the mapTypeId prop on render, clobbering an imperative
  // setMapTypeId, so the value must live in state and flow through the prop.
  const [activeMapType, setActiveMapType] = useState<string>(
    (mapTypeId as string) ?? "roadmap"
  );

  // Filter items with valid coordinates
  const validItems = useMemo(() => {
    return items.filter((item) => item.lat != null && item.lng != null);
  }, [items]);

  /*
    The legend's scale.

    ⚠️ Computed from the SAME `buildChoroplethScale` the fill uses, over the same inputs —
    it is a second CALL, never a second implementation. `MapContent` cannot hand its own
    copy up (the legend renders here, outside `<GoogleMap>`, because a React overlay
    inside the map's own children would be positioned by Google rather than by this card),
    so the one thing that must not happen is the two sides deriving bands independently: a
    legend describing a scale the map is not using is worse than no legend, because it is
    confidently wrong. The function is pure and both call sites pass the same config, so
    they cannot diverge without the function itself changing.
  */
  const choroplethLegendScale = useMemo(
    () =>
      choropleth
        ? buildChoroplethScale(
            choropleth.data,
            choropleth.kind,
            choropleth.minDenominator,
          )
        : null,
    [choropleth],
  );

  // Create markers for each item
  const markers = useMemo(() => {
    return validItems.map((item, index) => ({
      lat: item.lat,
      lng: item.lng,
      item,
      index,
      fillColor:
        "fillColor" in item
          ? (item.fillColor as string | undefined)
          : undefined,
      borderColor:
        "borderColor" in item
          ? (item.borderColor as string | undefined)
          : undefined,
      clusterPriority:
        "clusterPriority" in item
          ? (item.clusterPriority as number | undefined)
          : undefined,
      clusterColor:
        "clusterColor" in item
          ? (item.clusterColor as string | undefined)
          : undefined,
    }));
  }, [validItems]);

  /*
    Mirror the consumer's selection into the local index — and CLEAR it when they clear.

    The clear branch is new. This used to only ever SET an index, never unset one, which was
    survivable while the initial value was `0` (something was always "selected" anyway) and
    is a real bug now that it starts at `-1`: a consumer deselecting — clicking empty map,
    clearing a list row — would leave the last index stuck, so the pulse and tooltip stayed
    on a scan the user had just dismissed, and the pan-to-active effect kept a target.

    Also resets when the selected id is no longer in the item set, which is exactly what a
    filter change does: drawing a ring can remove the selected scan from `validItems`, and
    holding an index into a list that no longer contains it is how a stale overlay outlives
    its data.
  */
  useEffect(() => {
    if (!activeItemId) {
      setActiveIndex(-1);
      return;
    }
    const index = validItems.findIndex((item) => item.id === activeItemId);
    setActiveIndex(index);
  }, [activeItemId, validItems]);

  const handleMarkerClick = (index: number) => {
    setActiveIndex(index);
    if (onMarkerClick && validItems[index]) {
      onMarkerClick(validItems[index], index);
    }
  };

  // VERA-476: step through items from the overlay card itself. Previously the card was
  // static and the ONLY way to reach another item was clicking its marker — which is
  // fiddly on a phone and impossible for markers hidden inside a cluster.
  const stepActive = (delta: number) => {
    if (validItems.length < 2) return;
    // Wrap: the set is a closed list, and dead-ending at either end on a touch surface
    // reads as a broken swipe.
    const next = (activeIndex + delta + validItems.length) % validItems.length;
    setActiveIndex(next);
    if (onMarkerClick && validItems[next]) onMarkerClick(validItems[next], next);
  };

  // Horizontal swipe on the overlay card. Threshold keeps a sloppy vertical scroll from
  // registering as a step; the axis check lets the page scroll normally through the card.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const handleOverlayTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    swipeStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const handleOverlayTouchEnd = (e: React.TouchEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
    // Swipe LEFT (negative dx) advances, matching the direction of travel of a carousel.
    stepActive(dx < 0 ? 1 : -1);
  };

  // Set default center if no scans with location
  const mapCenter =
    (frameWorld ? WORLD_FRAME_CENTER : center) ||
    (validItems.length > 0
      ? { lat: validItems[0].lat, lng: validItems[0].lng }
      : { lat: 48.8566, lng: 2.3522 }); // Paris default

  // VRG-699: a `frameWorld` map takes its height from its width (the world's natural
  // ratio), so `height` is ignored for it. Every box below that used `height` reads this.
  const boxSizeSx = frameWorld
    ? { aspectRatio: String(WORLD_FRAME_ASPECT_RATIO), height: "auto" }
    : { height: typeof height === "number" ? `${height}px` : height };

  // Get map styles
  const styles = customStyles || MAP_STYLES[mapStyle] || MAP_STYLES.light;

  // Loading state
  if (isLoading && showLoadingState) {
    return (
      <Box
        sx={{
          ...boxSizeSx,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state - no API key
  if (!apiKey && showErrorState) {
    return (
      <Box
        sx={{
          ...boxSizeSx,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => theme.palette.background.default,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: (theme) => `${theme.shape.borderRadius}px` | 3,
        }}
      >
        <Typography variant="body1" color="error">
          {errorMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Docked overlay => this box is the CARD holding map + panel, so it grows with
        // its content and the fixed height moves to the inner map wrapper.
        // A `frameWorld` map's ratio lives on the inner wrapper, so this box wraps it.
        height:
          overlayPosition === "below" || frameWorld
            ? "auto"
            : typeof height === "number"
              ? `${height}px`
              : height,
        width: "100%",
        position: "relative",
        // 1x the shape token. `borderRadius: 3` is THREE times it in MUI's sx — 24px
        // against this app's 8px token, while every neighbouring control is ~10px, so
        // the map read as a much rounder object than anything around it (VERA-476).
        borderRadius: 1,
        // `overflow: hidden` is what makes the radius real: Google's .gm-style is
        // square-cornered and absolutely positioned, so without clipping it pokes
        // straight through the card's rounded corners.
        overflow: "hidden",
        ...(overlayPosition === "below" && {
          border: (theme: any) =>
            `1px solid ${theme.palette.primary?.[100] ?? theme.palette.divider}`,
        }),
        // Hide Google's default bottom chrome for a clean overlay: the attribution
        // ("Map data ©… / Terms / Report a map error"), the logo, and the keyboard-
        // shortcuts button (also disabled via keyboardShortcuts={false} on the Map).
        "& .gm-style-cc, & .gm-style-cc *": { display: "none !important" },
        "& a[href^='https://maps.google'], & a[href^='https://www.google.com/maps'], & img[alt='Google']":
          { display: "none !important" },
        // Hover popover: strip the InfoWindow's default white chrome / tail / close
        // button and paint a clean rounded bubble that FOLLOWS THE MAP — white on a
        // light map, dark on a dark map (so it never fights the tiles beneath it).
        "& .gm-style-iw-c": {
          padding: "0 !important",
          backgroundColor:
            mapStyle === "light" ? "#ffffff !important" : "#18181b !important",
          borderRadius: "8px !important",
          boxShadow:
            mapStyle === "light"
              ? "0 4px 16px rgba(0,0,0,0.18) !important"
              : "0 4px 16px rgba(0,0,0,0.4) !important",
          border:
            mapStyle === "light"
              ? "1px solid rgba(0,0,0,0.08)"
              : "1px solid rgba(255,255,255,0.08)",
        },
        "& .gm-style-iw-d": { overflow: "hidden !important", padding: "0 !important" },
        "& .gm-style-iw-tc, & .gm-style-iw-t::after": { display: "none !important" },
        "& .gm-style-iw button.gm-ui-hover-effect": { display: "none !important" },
        // Newer Maps wraps the content in a header row (.gm-style-iw-ch) that reserves
        // top padding for the (now-hidden) close button — zero it so the bubble isn't
        // top-heavy.
        "& .gm-style-iw-ch": { paddingTop: "0 !important" },
        "& .gm-style-iw-chr": { display: "none !important" },
        "& .vera-map-tip": {
          color: mapStyle === "light" ? "#18181b" : "#fafafa",
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.4,
          padding: "10px 14px",
          whiteSpace: "nowrap",
        },
        // Content sub-elements (emitted by consumers via these classes) follow the map
        // too, so rich tooltips theme themselves instead of hardcoding dark colors.
        "& .vera-map-tip .vmt-name": {
          color: mapStyle === "light" ? "#18181b" : "#fafafa",
        },
        "& .vera-map-tip .vmt-sub": {
          color: mapStyle === "light" ? "#52525b" : "#a1a1aa",
        },
        "& .vera-map-tip .vmt-avatar": {
          backgroundColor: mapStyle === "light" ? "#e4e4e7" : "#3f3f46",
          color: mapStyle === "light" ? "#3f3f46" : "#fafafa",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          // No radius when docked — the outer card already clips, and rounding here
          // would carve a visible notch above the panel.
          borderRadius: overlayPosition === "below" ? 0 : 1,
          ...boxSizeSx,
        }}
      >
      <APIProvider apiKey={apiKey} libraries={MAP_EXTRA_LIBRARIES}>
        <GoogleMap
          // A `frameWorld` map starts zoomed out; `applyFloor` then sets the exact
          // one-world zoom, so there is no zoom-4 flash first.
          defaultZoom={frameWorld ? MIN_WORLD_ZOOM : defaultZoom}
          defaultCenter={mapCenter}
          // Tile-load placeholder color — dark in dark mode so panning/zooming to a
          // new area doesn't flash WHITE while tiles load.
          backgroundColor={mapStyle === "dark" ? "#18181b" : "#e8eaed"}
          mapId={mapId}
          mapTypeId={activeMapType as any}
          fullscreenControl={false}
          styles={styles as any}
          disableDefaultUI={disableDefaultUI}
          zoomControl={false}
          streetViewControl={false}
          mapTypeControl={false}
          keyboardShortcuts={false}
          /*
            Armed freehand suppresses map gestures entirely, DECLARATIVELY rather than by
            an imperative `setOptions` inside the draw layer. @vis.gl re-applies its props
            on every render (the same clobbering the `activeMapType` note above records
            for `setMapTypeId`), so an imperative override could be reverted mid-stroke —
            the map would start panning under a half-drawn ring. `MapDrawLayer` also sets
            `draggable: false`, which is what covers a consumer rendering the layer
            directly rather than through this component.
          */
          gestureHandling={drawMode === "freehand" ? "none" : gestureHandling}
          /*
            No `minZoom` here on purpose — it is set imperatively per container width by
            the effect in `MapContent` (search `worldFitMinZoom`).

            ⚠️ This used to be `minZoom={2}` with a comment asserting that
            `strictBounds` made Google "clamp zoom-out so you can't zoom out past one
            full world copy". That is NOT what strictBounds does, and the page showed it:
            one world is 256 * 2^zoom px, so at zoom 2 it is 1024px, and in a ~1650px
            card the user got two and a half copies of the planet side by side. Google
            does not raise the floor per container size — nothing did.

            A FIXED floor cannot be right, because the zoom at which one world fills the
            viewport depends entirely on how wide the viewport is. Hence the effect.
          */
          restriction={{
            latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
            // Off for `frameWorld` only: strict bounds forbid zoom < 1, which a narrow
            // whole-world map needs. That map clamps its own viewport instead (see the
            // `clampToWorld` effect in `MapContent`).
            strictBounds: !frameWorld,
          }}
        >
          <MapContent
            items={markers}
            onMarkerClick={handleMarkerClick}
            clusterRenderer={clusterRenderer}
            enableClustering={enableClustering}
            // Effective active id, not just the prop: stepping the overlay card
            // (swipe / prev / next) moves `activeIndex` internally, and MapContent pans
            // on activeItemId change. Passing only the prop meant the card advanced to
            // another city while the map stayed where it was.
            activeItemId={validItems[activeIndex]?.id ?? activeItemId}
            fitToItems={fitToItems}
            suppressFit={suppressFit}
            fitSignature={fitSignature}
            renderMode={renderMode}
            choropleth={choropleth}
            onChoroplethUnavailable={onChoroplethUnavailable}
            onHeatUnavailable={onHeatUnavailable}
            onDeselect={onDeselect}
            renderStackTooltip={renderStackTooltip}
            onStackClick={onStackClick}
            path={path}
            pathColor={pathColor}
            relayoutNonce={relayoutNonce}
            // Aggregation pills invert on a dark basemap — see `MapContent`'s own prop.
            mapStyle={mapStyle}
            frameWorld={frameWorld}
          />
          {/*
            Area selection (VRG-480). Renders `drawnRing` in EVERY mode, so a restored
            saved view shows its shape without the tool being armed — a filter narrowing
            every figure on the page with nothing on the map to explain it is the one
            outcome worse than not persisting it at all.
          */}
          <MapPulseLayer items={validItems} enabled={pulseItems} renderMode={renderMode} />
          <MapDrawLayer
            mode={drawMode}
            ring={drawnRing}
            onDrawComplete={onDrawComplete}
            onDrawCancel={onDrawCancel}
            color={drawColor ?? DEFAULT_PIN_COLOR}
          />
          <MapZoomControls
            mapType={activeMapType}
            onSelectLayer={setActiveMapType}
            // Anchor to the TOP when a bottom overlay is in play — otherwise the
            // selected-item card sits directly on top of the controls and the layers
            // button is only half visible behind it.
            position={
              renderOverlay && overlayPosition.startsWith("bottom")
                ? ControlPosition.RIGHT_TOP
                : ControlPosition.RIGHT_BOTTOM
            }
          />
        </GoogleMap>
      </APIProvider>

      {/*
        ── THE SCALE KEY (VERA-260) ───────────────────────────────────────────────────

        Rendered by the MAP, not left to the consumer, because a choropleth without a
        legend is not a degraded chart — it is an undecodable one. The entire encoding is
        colour → magnitude, so with no key the reader has a picture of relative darkness
        and no way to turn it into a number. Making it the consumer's job would make it
        forgettable, and a consumer who forgot would ship a picture that looks like data.

        Bottom-left, which it can hold uncontested in this mode: `renderOverlay` draws
        only for a SELECTED item, and choropleth mode has no markers to select. The zoom
        and layer controls live on the right.
      */}
      {renderMode === "choropleth" && choropleth && choroplethLegendScale && (
        <Box
          sx={{
            position: "absolute",
            left: 16,
            bottom: 16,
            zIndex: 2,
            // The map owns clicks/drags underneath; the legend is a key, not a control.
            pointerEvents: "none",
          }}
        >
          <MapChoroplethLegend
            label={choropleth.label}
            kind={choropleth.kind}
            scale={choroplethLegendScale}
            insufficientLabel={
              choropleth.minDenominator
                ? // See `MapChoroplethConfig.insufficientLabel` — the consumer names the
                  // unit because only it knows what its denominator is. The fallback is
                  // right for a rate over scans and wrong for any other.
                  (choropleth.insufficientLabel ??
                  `Fewer than ${choropleth.minDenominator.toLocaleString()} scans`)
                : undefined
            }
            footnote={choropleth.footnote}
          />
        </Box>
      )}

      {/*
        Empty state overlay — centered card, matches table empty state style.

        ⚠️ In CHOROPLETH mode "empty" is a property of `choropleth.data`, not of `items`.
        The card's item set is scan POINTS, which this mode does not draw at all, so
        keying the empty state off it would have shown "no locations available" over a
        perfectly good country map any time the consumer stopped passing points — and,
        the other way round, shown nothing at all when the country data was genuinely
        empty.
      */}
      {(renderMode === "choropleth"
        ? !choropleth || choropleth.data.length === 0
        : validItems.length === 0) && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            // ABSOLUTE px, not spacing factors — working-conventions §47. This is a
            // LAYER-1 component and the two apps have different spacing bases
            // (@repo/ui 4px, apps/app never overrides MUI's default 8px), so the
            // previous `px: 4, py: 3` rendered 16/12 on platform and 32/24 in
            // apps/app. The comment below was written against the platform numbers
            // and was true there, which is exactly why the app's version stayed
            // oversized without anyone noticing: the code looked already-fixed.
            //
            // Tightened: the card was carrying more padding than its one line of text
            // warranted, so it read as an oversized modal rather than a quiet inline
            // notice. Radius matches the map controls and the app's buttons (10px).
            px: '16px',
            py: '12px',
            borderRadius: "10px",
            boxShadow: (theme) => theme.shadows[2],
            textAlign: "center",
            // `nowrap` forced the card as wide as the message, which is what made it
            // overflow narrow map cards. Wrap instead, bounded by the map's width.
            maxWidth: "calc(100% - 32px)",
          }}
        >
          {/*
            `body2` (16px), not `body1` (20px in this theme). An empty-state overlay is
            a caption on a surface the reader is already looking at, not a headline —
            at 20px it competed with the map itself and forced the card wider than the
            message needed. Applies to every Map consumer deliberately: an oversized
            placeholder was oversized everywhere, not just on the analytics card.
          */}
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      )}

      </Box>

      {/* Custom overlay for active item */}
      {renderOverlay &&
        validItems.length > 0 &&
        activeIndex >= 0 &&
        validItems[activeIndex] && (
          <Box
            sx={{
              // `below` docks the panel under the map in normal flow; every other value
              // floats it over the tiles.
              position: overlayPosition === "below" ? "static" : "absolute",
              ...(overlayPosition === "below" && {
                // Flush: no top margin, so the panel is attached to the map rather than
                // floating below it as a separate card.
                mt: 0,
                width: "100%",
                maxWidth: "100%",
              }),
              ...(overlayPosition === "top-left" && { top: 16, left: 16 }),
              ...(overlayPosition === "top-right" && { top: 16, right: 16 }),
              ...(overlayPosition === "bottom-left" && {
                left: { xs: "50%", sm: 16 },
                bottom: { xs: 24, sm: 16 },
                transform: { xs: "translateX(-50%)", sm: "none" },
              }),
              ...(overlayPosition === "bottom-right" && {
                bottom: 16,
                right: 16,
              }),
              ...(overlayPosition === "below"
                ? {
                    // Docked: a single top divider separates it from the map. No border,
                    // radius or shadow — the outer card provides all three, and adding
                    // them here would draw a second box inside the first.
                    borderTop: (theme: any) =>
                      `1px solid ${
                        theme.palette.primary?.[100] ?? theme.palette.divider
                      }`,
                  }
                : {
                    maxWidth: "300px",
                    width: { xs: "calc(100% - 32px)", sm: "300px" },
                    boxShadow: (theme: any) => theme.shadows[8],
                  }),
              backgroundColor: (theme) => theme.palette.background.paper,
              ...(overlayPosition !== "below" && {
                borderRadius: (theme: any) => `${theme.shape.borderRadius}px`,
              }),
              overflow: "hidden",
            }}
            onTouchStart={handleOverlayTouchStart}
            onTouchEnd={handleOverlayTouchEnd}
          >
            {renderOverlay(validItems[activeIndex])}

            {/* Prev / next + position. Only when there is somewhere to go.
                Chevrons rather than dots: a certificate can have dozens of scans, and
                dots stop being countable (or tappable) past a handful. The counter
                carries the "there are more" signal that dots would otherwise give, and
                it also tells the user WHERE they are, which swipe alone never does. */}
            {validItems.length > 1 && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={(theme) => ({
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${
                    theme.palette.primary?.[100] ?? theme.palette.divider
                  }`,
                })}
              >
                <IconButton
                  aria-label="Previous scan"
                  size="small"
                  onClick={() => stepActive(-1)}
                  sx={{ color: "text.secondary" }}
                >
                  <ChevronLeft size={18} strokeWidth={1.5} />
                </IconButton>

                <Typography variant="caption" color="text.secondary">
                  {activeIndex + 1} of {validItems.length}
                </Typography>

                <IconButton
                  aria-label="Next scan"
                  size="small"
                  onClick={() => stepActive(1)}
                  sx={{ color: "text.secondary" }}
                >
                  <ChevronRight size={18} strokeWidth={1.5} />
                </IconButton>
              </Stack>
            )}
          </Box>
        )}
    </Box>
  );
};
